import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface TimeRange {
  start: string; // "09:00"
  end: string;   // "18:00"
}

export interface DaySchedule {
  enabled: boolean;
  ranges: TimeRange[];
}

export type WeekSchedule = Record<string, DaySchedule>;

const defaultSchedule: WeekSchedule = {
  "0": { enabled: false, ranges: [{ start: "09:00", end: "18:00" }] },
  "1": { enabled: true, ranges: [{ start: "09:00", end: "12:00" }, { start: "14:00", end: "18:00" }] },
  "2": { enabled: true, ranges: [{ start: "09:00", end: "12:00" }, { start: "14:00", end: "18:00" }] },
  "3": { enabled: true, ranges: [{ start: "09:00", end: "12:00" }, { start: "14:00", end: "18:00" }] },
  "4": { enabled: true, ranges: [{ start: "09:00", end: "12:00" }, { start: "14:00", end: "18:00" }] },
  "5": { enabled: true, ranges: [{ start: "09:00", end: "12:00" }, { start: "14:00", end: "18:00" }] },
  "6": { enabled: false, ranges: [{ start: "09:00", end: "18:00" }] },
};

const SLOT_INTERVAL = 30; // minutes

export const generateTimeSlotsFromRanges = (ranges: TimeRange[]): string[] => {
  const slots: string[] = [];
  for (const range of ranges) {
    const [startH, startM] = range.start.split(":").map(Number);
    const [endH, endM] = range.end.split(":").map(Number);
    let current = startH * 60 + startM;
    const end = endH * 60 + endM;
    while (current < end) {
      const h = Math.floor(current / 60).toString().padStart(2, "0");
      const m = (current % 60).toString().padStart(2, "0");
      slots.push(`${h}:${m}`);
      current += SLOT_INTERVAL;
    }
  }
  return slots;
};

export const generateTimeSlotsForDay = (schedule: DaySchedule): string[] => {
  if (!schedule.enabled) return [];
  return generateTimeSlotsFromRanges(schedule.ranges);
};

export interface StaffMember {
  id: string;
  name: string;
  is_active: boolean;
  working_hours: WeekSchedule;
}

export const useStaffMembers = () => {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("staff_public")
      .select("id, name, is_active, working_hours")
      .eq("is_active", true)
      .then(({ data }) => {
        const members = (data || []).map((s: any) => ({
          id: s.id,
          name: s.name,
          is_active: s.is_active,
          working_hours: typeof s.working_hours === "string"
            ? JSON.parse(s.working_hours)
            : s.working_hours || defaultSchedule,
        }));
        setStaff(members);
        setLoading(false);
      });
  }, []);

  const getTimeSlotsForDate = (date: Date, staffId?: string): string[] => {
    const dayOfWeek = date.getDay().toString();
    if (staffId) {
      const member = staff.find(s => s.id === staffId);
      if (!member) return [];
      const daySchedule = member.working_hours[dayOfWeek];
      return daySchedule?.enabled ? generateTimeSlotsFromRanges(daySchedule.ranges) : [];
    }
    // Merge all staff slots
    const allSlots = new Set<string>();
    staff.forEach(member => {
      const daySchedule = member.working_hours[dayOfWeek];
      if (daySchedule?.enabled) {
        generateTimeSlotsFromRanges(daySchedule.ranges).forEach(s => allSlots.add(s));
      }
    });
    return Array.from(allSlots).sort();
  };

  const isDayEnabled = (date: Date, staffId?: string): boolean => {
    const dayOfWeek = date.getDay().toString();
    if (staffId) {
      const member = staff.find(s => s.id === staffId);
      return member?.working_hours[dayOfWeek]?.enabled ?? false;
    }
    return staff.some(member => member.working_hours[dayOfWeek]?.enabled);
  };

  return { staff, loading, getTimeSlotsForDate, isDayEnabled };
};

// Keep backward compatibility
export const useWorkingHours = () => {
  const { staff, loading, getTimeSlotsForDate, isDayEnabled } = useStaffMembers();
  
  // Merge all staff schedules into one
  const schedule: WeekSchedule = { ...defaultSchedule };
  if (staff.length > 0) {
    for (let d = 0; d < 7; d++) {
      const key = d.toString();
      const enabled = staff.some(s => s.working_hours[key]?.enabled);
      const ranges: TimeRange[] = [];
      staff.forEach(s => {
        if (s.working_hours[key]?.enabled) {
          ranges.push(...s.working_hours[key].ranges);
        }
      });
      schedule[key] = { enabled, ranges };
    }
  }

  return { schedule, loading, getTimeSlotsForDate, isDayEnabled };
};

export { defaultSchedule };
